import React, { useState, useEffect, useRef } from "react";
import {
  Table, TableContainer, TableHead, TableBody, TableRow, TableCell,
  Paper, TextField, IconButton, InputAdornment, TablePagination, Typography,
  Tooltip, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, InputLabel, MenuItem, Select,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import EventNoteIcon from "@mui/icons-material/EventNote";
import { instance } from "../../axios";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Fab from "@mui/material/Fab";
import LinearProgress from "@mui/material/LinearProgress";
import {
  getPatientsAction,
  cleanPatientAction,
  getPatientAction,
  setOffset,
  setQuery,
  setNoSubscription,
  setModuleFilter,
  updatePatientAction,
} from "../../redux/actions/patient";
import Chip from "@mui/material/Chip";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import strings from "../../i18n/language";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import ManIcon from "@mui/icons-material/Man";
import WomanIcon from "@mui/icons-material/Woman";
import ChildCareIcon from "@mui/icons-material/ChildCare";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SearchIcon from "@mui/icons-material/Search";
import PatientDelete from "../../dialog/PatientDelete";
import dayjs from "dayjs";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Collapse from "@mui/material/Collapse";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import NotInterestedIcon from "@mui/icons-material/NotInterested";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

const PatientList = () => {
  const patients = useSelector((state) => state.patient.patients);
  const [patientDeleteId, setPatientDeleteId] = useState(null);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [visitDialogPatient, setVisitDialogPatient] = useState(null);
  const [visitType, setVisitType] = useState("evaluacion");
  const [visitDate, setVisitDate] = useState(dayjs());
  const [creatingVisit, setCreatingVisit] = useState(false);
  const total = useSelector((state) => state.patient.total);
  const limit = useSelector((state) => state.patient.limit) || 20;
  const user = useSelector((state) => state.user.user);
  const noSubscription = useSelector((state) => state.patient.noSubscription);
  const moduleFilter = useSelector((state) => state.patient.moduleFilter);
  const theme = useTheme();

  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);

  const parseModules = (modules) => {
    if (Array.isArray(modules)) return modules;
    if (typeof modules === "string") { try { return JSON.parse(modules); } catch { return []; } }
    return [];
  };

  const dispatch = useDispatch();
  const patientsRef = useRef([]);
  const navigate = useNavigate();
  const isLoadingGetPatients = useSelector(
    (state) => state.patient.isLoadingGetPatients
  );

  useEffect(() => {
    dispatch(getPatientsAction());
  }, [dispatch]);

  useEffect(() => {
    if (patients) patientsRef.current = patients;
  }, [patients]);

  useEffect(() => {
    setSearching(true);
    setPage(0);
    const timeoutId = setTimeout(() => {
      setSearching(false);
      dispatch(setQuery(searchQuery));
    }, 1500);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, dispatch]);

  const handleCreateVisit = async () => {
    if (!visitDialogPatient) return;
    setCreatingVisit(true);
    try {
      const res = await instance.post("/visit/", {
        patientId: visitDialogPatient.id,
        userId: user.id,
        type: visitType,
        visitDate: visitDate.toISOString(),
      });
      setVisitDialogPatient(null);
      navigate(`/visits/${res.data.id}`);
    } catch {
      // keep dialog open on error
    } finally {
      setCreatingVisit(false);
    }
  };

  const showSubscriptionsStatus = (patient) => {
    if (!patient.PatientSubscriptions.length) {
      return (
        <Tooltip title={strings.LABEL_NO_SUBSCRIPTION}>
          <NotInterestedIcon
            fontSize="small"
            style={{ color: theme.palette.error.main }}
          />
        </Tooltip>
      );
    }
    return patient.PatientSubscriptions.map((patientSubscription) => {
      if (!patientSubscription.Subscription?.validity)
        return (
          <Tooltip key={"question" + patientSubscription.id} title={strings.LABEL_MISSING}>
            <QuestionMarkIcon
              style={{ color: theme.palette.warning.main }}
            />
          </Tooltip>
        );
      else {
        const isExpired = dayjs(patientSubscription.Subscription?.validity).isBefore(dayjs());
        const color = isExpired ? theme.palette.warning.main : theme.palette.success.main;
        switch (patientSubscription.Subscription?.type) {
          case "individual":
            return (
              <Tooltip key={"individual" + patientSubscription.id} title={strings.LABEL_INDIVIDUAL}>
                <PersonIcon style={{ color }} />
              </Tooltip>
            );
          case "family":
            return patient.parent_id ? (
              <Tooltip key={"family" + patientSubscription.id} title={strings.LABEL_FAMILY_GROUP}>
                <PersonOutlineIcon style={{ color }} />
              </Tooltip>
            ) : (
              <Tooltip key={"family" + patientSubscription.id} title={strings.LABEL_HEAD_FAMILY_GROUP}>
                <GroupsIcon style={{ color }} />
              </Tooltip>
            );
          case "company":
            return (
              <Tooltip key={"company" + patientSubscription.id} title={strings.LABEL_COMPANY}>
                <BusinessIcon style={{ color }} />
              </Tooltip>
            );
          default:
            return "Other";
        }
      }
    });
  };

  const Row = ({ patient }) => {
    const [open, setOpen] = useState(false);
    let age = patient.birthDate
      ? dayjs().diff(dayjs(patient.birthDate), "year")
      : null;
    const displayType = (patientSubscription) => {
      if (patientSubscription.Subscription?.type === "individual") {
        return strings.LABEL_INDIVIDUAL;
      } else if (patientSubscription.Subscription?.type === "family") {
        return patient.parent_id && patient.Parent
          ? strings.LABEL_FAMILY_GROUP + ` (${patient.Parent.displayName})`
          : strings.LABEL_HEAD_FAMILY_GROUP;
      } else if (patientSubscription.Subscription?.type === "company") {
        return (
          strings.LABEL_COMPANY +
          ` (${patientSubscription.Subscription.Company?.name || ""})`
        );
      } else {
        return "Other";
      }
    };
    return (
      <>
        <TableRow
          sx={{
            backgroundColor: patient.archive
              ? theme.palette.action.disabledBackground
              : "inherit",
            opacity: patient.archive ? 0.6 : 1,
            cursor: "pointer",
            "&:hover td, &:hover th": { backgroundColor: theme.palette.action.hover },
          }}
          onClick={() => navigate(`/patients/${patient.id}`)}
        >
          <TableCell onClick={(e) => e.stopPropagation()}>
            {patient.PatientSubscriptions.length > 0 && (
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
              >
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            )}
          </TableCell>
          <TableCell component="th" scope="row" style={{ opacity: 0.7 }}>
            {age !== null && age < 18 ? (
              <ChildCareIcon />
            ) : patient.gender === "man" ? (
              <ManIcon />
            ) : (
              <WomanIcon />
            )}
            ({patient.id})
          </TableCell>
          <TableCell>{patient.displayName}</TableCell>
          {user.type !== "medecin" && (
            <TableCell>
              {showSubscriptionsStatus(patient)}
            </TableCell>
          )}
          <TableCell sx={{ whiteSpace: "nowrap" }}>{patient.identityNumber}</TableCell>
          <TableCell>
            <div
              style={{
                flexDirection: "row",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography>{age !== null ? age : "-"}</Typography>
            </div>
          </TableCell>
          <TableCell>{patient.phone}</TableCell>
          <TableCell sx={{ whiteSpace: "nowrap" }}>
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
              {parseModules(patient.modules).map((mod) => (
                <Chip key={mod} label={mod} size="small" color="primary" variant="outlined" />
              ))}
            </Box>
          </TableCell>
          <TableCell
            sx={{ whiteSpace: "nowrap" }}
            onClick={(e) => e.stopPropagation()}
          >
            {user.type !== "viewer" && (
              <Tooltip title="Nueva visita">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setVisitType("evaluacion");
                    setVisitDate(dayjs());
                    setVisitDialogPatient(patient);
                  }}
                >
                  <EventNoteIcon />
                </IconButton>
              </Tooltip>
            )}
            {user.type !== "viewer" && user.type !== "medecin" && (
              <IconButton
                aria-label="delete"
                onClick={(e) => {
                  e.stopPropagation();
                  setPatientDeleteId(patient.id);
                  setOpenModalDelete(true);
                }}
              >
                <DeleteIcon />
              </IconButton>
            )}
            {user.type !== "viewer" && (
              <IconButton
                aria-label={patient.archive ? "unarchive" : "archive"}
                onClick={(e) => {
                  e.stopPropagation();
                  const isArchiving = !patient.archive;
                  dispatch(updatePatientAction({ id: patient.id, archive: isArchiving }))
                    .then(() => {
                      toast.success(isArchiving ? strings.MESSAGE_PATIENT_ARCHIVED : strings.MESSAGE_PATIENT_UNARCHIVED);
                    })
                    .catch(() => {
                      toast.error(strings.ERROR_ARCHIVE_PATIENT);
                    });
                }}
              >
                {patient.archive ? <UnarchiveIcon /> : <ArchiveIcon />}
              </IconButton>
            )}
            {user.type !== "viewer" && (
              <IconButton
                aria-label="edit"
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(getPatientAction(patient.id));
                  navigate("/patients/edit");
                }}
              >
                <EditIcon />
              </IconButton>
            )}
          </TableCell>
        </TableRow>
        {patient.PatientSubscriptions.length > 0 && (
          <TableRow>
            <TableCell
              style={{ paddingBottom: 0, paddingTop: 0, borderBottom: "none" }}
              colSpan={8}
            >
              <Collapse in={open} timeout="auto" unmountOnExit>
                <Box sx={{ margin: 1 }}>
                  <Typography variant="h6" gutterBottom component="div">
                    {strings.MESSAGE_LIST_SUBSCRIPTIONS}
                  </Typography>
                  <Table size="small" aria-label="purchases" colSpan={8}>
                    <TableHead>
                      <TableRow>
                        <TableCell colSpan={2}>{strings.LABEL_TYPE}</TableCell>
                        <TableCell>{strings.LABEL_START}</TableCell>
                        <TableCell>{strings.LABEL_VALIDITY}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {patient.PatientSubscriptions.map(
                        (patientSubscription) => {
                          return (
                            <TableRow
                              key={
                                patientSubscription.patient_id +
                                "-" +
                                patientSubscription.subscription_id
                              }
                            >
                              <TableCell>
                                {patientSubscription.Subscription?.type ===
                                "individual" ? (
                                  <PersonIcon></PersonIcon>
                                ) : patientSubscription.Subscription?.type ===
                                  "family" ? (
                                  <GroupsIcon></GroupsIcon>
                                ) : patientSubscription.Subscription?.type ===
                                  "company" ? (
                                  <BusinessIcon></BusinessIcon>
                                ) : (
                                  "Other"
                                )}
                              </TableCell>
                              <TableCell>
                                {displayType(patientSubscription)}
                              </TableCell>
                              <TableCell>
                                {patientSubscription.Subscription?.start
                                  ? dayjs(
                                      patientSubscription.Subscription.start
                                    ).format("LL")
                                  : ""}
                              </TableCell>
                              <TableCell>
                                {patientSubscription.Subscription?.validity
                                  ? dayjs(
                                      patientSubscription.Subscription.validity
                                    ).format("LL")
                                  : ""}
                              </TableCell>
                            </TableRow>
                          );
                        }
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        )}
      </>
    );
  };

  return (
    <>
      <ToastContainer position="bottom-right" />
      <Paper sx={{ p: 2, width: "80%" }}>
        {(searching || isLoadingGetPatients) && (
          <LinearProgress
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              width: "100%",
            }}
          />
        )}
        <TextField
          fullWidth
          variant="outlined"
          placeholder={strings.LABEL_SEARCH}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onBlur={() => {
            dispatch(setQuery(searchQuery));
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            sx: { borderRadius: 25 }, // Rounded corners for input field
          }}
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
          <Chip
            icon={<NotInterestedIcon />}
            label={strings.LABEL_NO_SUBSCRIPTION}
            color={noSubscription ? "primary" : "default"}
            variant={noSubscription ? "filled" : "outlined"}
            onClick={() => {
              setPage(0);
              dispatch(setNoSubscription(!noSubscription));
            }}
          />
          {["Servital", "Laboral"].map((mod) => (
            <Chip
              key={mod}
              label={mod}
              color={moduleFilter === mod ? "primary" : "default"}
              variant={moduleFilter === mod ? "filled" : "outlined"}
              onClick={() => dispatch(setModuleFilter(moduleFilter === mod ? null : mod))}
            />
          ))}
        </Box>

        {patients && (
          <TableContainer>
            <Table aria-label="Patients table">
              <TableHead>
                <TableRow>
                  <TableCell colSpan={2}>{strings.LABEL_PATIENTS}</TableCell>
                  <TableCell>{strings.LABEL_NAME}</TableCell>
                  {user.type !== "medecin" && <TableCell>{strings.LABEL_STATUS}</TableCell>}
                  <TableCell>{strings.LABEL_IDENTITY}</TableCell>
                  <TableCell>{strings.LABEL_AGE}</TableCell>
                  <TableCell>{strings.LABEL_PHONE}</TableCell>
                  <TableCell>Modules</TableCell>
                  {user.type !== "viewer" && (
                    <TableCell>{strings.LABEL_ACTION}</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {patients.map((patient) => (
                    <Row key={patient.id} patient={patient}></Row>
                  ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[limit]}
              count={total}
              rowsPerPage={limit}
              page={page}
              onPageChange={(event, newPage) => {
                dispatch(setOffset(limit * newPage));
                setPage(newPage);
              }}
              component="div"
            />
          </TableContainer>
        )}
      </Paper>
      {user.type !== "viewer" && (
        <Fab
          style={{ alignSelf: "flex-end", marginLeft: 10 }}
          color="primary"
          aria-label="add"
          onClick={() => {
            dispatch(cleanPatientAction());
            navigate("/patients/edit");
          }}
        >
          <AddIcon />
        </Fab>
      )}
      <PatientDelete
        open={openModalDelete}
        setOpen={setOpenModalDelete}
        patientDeleteId={patientDeleteId}
      ></PatientDelete>

      {/* ── Nueva visita dialog ── */}
      <Dialog open={!!visitDialogPatient} onClose={() => setVisitDialogPatient(null)} fullWidth maxWidth="xs">
        <DialogTitle>Nueva visita — {visitDialogPatient?.displayName}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Tipo de visita</InputLabel>
            <Select value={visitType} label="Tipo de visita" onChange={(e) => setVisitType(e.target.value)}>
              <MenuItem value="consulta" disabled>Consulta general</MenuItem>
              <MenuItem value="evaluacion">Consulta médica ocupacional</MenuItem>
            </Select>
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
            <DatePicker
              label="Fecha de la visita"
              value={visitDate}
              onChange={setVisitDate}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVisitDialogPatient(null)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreateVisit} disabled={creatingVisit}>
            {creatingVisit ? <CircularProgress size={18} /> : "Crear visita"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PatientList;
